// Velocity & predictability — pure-logic library for Phase 1b-3.
//
// Reads M1 (Total SP delivered) and M5 (Planned SP) from each sprint's
// headline_metrics dict. All inputs are SprintListItem[] sorted newest-first
// (matches what the API returns). All series outputs are oldest→newest
// (matches Chart.js axis convention).

import type { MetricEnvelope, SprintListItem } from "@/api/types";

const DEFAULT_WINDOW = 8;
const DEFAULT_ROLLING = 3;
const DEFAULT_TOLERANCE = 0.10;

export interface VelocityPoint {
  sprintId: number;
  sprintName: string;
  planned: number | null;
  delivered: number | null;
}

export type PredictabilityVerdict = "within" | "over" | "under" | "unknown";

export interface PredictabilityRate {
  rate: number | null; // share of sprints within tolerance, or null if no qualifying sprints
  withinCount: number;
  qualifyingCount: number; // sprints with both planned > 0 and delivered != null
}

function readSp(env: MetricEnvelope | undefined): number | null {
  if (!env) return null;
  const v = env.value;
  return typeof v === "number" ? v : null;
}

/**
 * Last `n` closed sprints' planned-vs-delivered SP, in chronological order
 * (oldest → newest). Active and future sprints are excluded — they don't have
 * meaningful "delivered" yet.
 */
export function velocitySeries(
  sprints: SprintListItem[],
  n: number = DEFAULT_WINDOW,
): VelocityPoint[] {
  const closed = sprints.filter((s) => s.sprint_state === "closed");
  const window = closed.slice(0, n).reverse();
  return window.map((s) => ({
    sprintId: s.sprint_id,
    sprintName: s.sprint_name,
    planned: readSp(s.headline_metrics?.M5),
    delivered: readSp(s.headline_metrics?.M1),
  }));
}

/**
 * Trailing rolling average of `window` size, applied to a numeric series.
 * Null inputs propagate as null in the matching output position. Earliest
 * (window-1) outputs are null because there isn't enough history yet.
 */
export function rollingAverage(
  values: Array<number | null>,
  window: number = DEFAULT_ROLLING,
): Array<number | null> {
  if (window < 1) return values.slice();
  return values.map((_, i) => {
    if (i + 1 < window) return null;
    const slice = values.slice(i + 1 - window, i + 1);
    if (slice.some((v) => v == null)) return null;
    const sum = slice.reduce((acc, v) => (acc as number) + (v as number), 0) as number;
    return sum / window;
  });
}

/**
 * Verdict for one sprint: how delivered SP relates to planned SP under the
 * given tolerance. "within" iff |delivered - planned| / planned ≤ tolerance.
 */
export function predictability(
  planned: number | null,
  delivered: number | null,
  tolerance: number = DEFAULT_TOLERANCE,
): PredictabilityVerdict {
  if (planned == null || delivered == null || planned <= 0) return "unknown";
  const ratio = (delivered - planned) / planned;
  if (Math.abs(ratio) <= tolerance) return "within";
  return ratio > 0 ? "over" : "under";
}

/**
 * Aggregate predictability rate across a velocity series. Sprints without
 * planned SP > 0 or without delivered SP are ignored from both numerator and
 * denominator (they can't be judged).
 */
export function predictabilityRate(
  series: VelocityPoint[],
  tolerance: number = DEFAULT_TOLERANCE,
): PredictabilityRate {
  let within = 0;
  let qualifying = 0;
  for (const p of series) {
    const verdict = predictability(p.planned, p.delivered, tolerance);
    if (verdict === "unknown") continue;
    qualifying += 1;
    if (verdict === "within") within += 1;
  }
  return {
    rate: qualifying === 0 ? null : within / qualifying,
    withinCount: within,
    qualifyingCount: qualifying,
  };
}
